import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import TaskTimeInput from '@/components/TaskTimeInput';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {Textarea} from '@/components/ui/textarea';
import type {MedicationForm} from '@/services/graphqlHooks';
import {MedicationFoodRelation} from '@/services/graphqlHooks';
import medicationFoodRelation from '@/translation/medicationFoodRelation';

import type {ResidentMedicationDosageFormValues} from '../utils/medicationFormInputs';
import MedicationUnitSelect from './resident-medication-form-fields/MedicationUnitSelect';

export type ResidentMedicationDosageFormFieldsProps = {
  form: UseFormReturn<ResidentMedicationDosageFormValues>;
  selectedMedicationForm?: MedicationForm;
};

const relationToFoodOptions = Object.values(MedicationFoodRelation);

const ResidentMedicationDosageFormFields = ({
  form,
  selectedMedicationForm,
}: ResidentMedicationDosageFormFieldsProps) => {
  const {t} = useTranslation();

  return (
    <>
      <FormRequiredLegend />

      <div className="flex gap-4 max-sm:flex-col">
        <FormField
          control={form.control}
          name="dosageQuantity"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel required>{t('Quantity')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  value={field.value ?? ''}
                  onChange={e =>
                    field.onChange(
                      e.target.value === '' ? undefined : e.target.value
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dosageUnit"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel required>{t('Unit')}</FormLabel>
              <FormControl>
                <MedicationUnitSelect
                  onChange={field.onChange}
                  selectedMedicationForm={selectedMedicationForm}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="time"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Time')}</FormLabel>
            <FormControl>
              <TaskTimeInput
                onChange={field.onChange}
                isHourInterval={false}
                value={field.value || ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="relationToFood"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Relation to food')}</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ''}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null">{t('None')}</SelectItem>
                  {relationToFoodOptions.map(foodRelation => (
                    <SelectItem key={foodRelation} value={foodRelation}>
                      {medicationFoodRelation[foodRelation] || foodRelation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="customInstructions"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Additional instruction')}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default ResidentMedicationDosageFormFields;
